import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { TABLE_CONFIGS } from "./tableConfigs";
import ShipmentCard from "./ShipmentCard";
import InvoiceCard from "./InvoiceCard";
import OrderCard from "./OrderCard";

/**
 * EnhancedDataTable - Professional table component for list data
 * Features:
 * - Configurable columns per entity type
 * - Expandable rows with detail cards
 * - Badge rendering for status fields
 * - Custom formatters for dates, currency, routes
 * - Clicking a row toggles expansion; clicking the card in expanded view triggers navigation
 *
 * @param {Object} props
 * @param {Object|Array} props.data - The data to display (object with array property or raw array)
 * @param {string} props.responseType - The response type key for TABLE_CONFIGS
 * @param {Function} props.onRowClick - Optional callback when card in expanded view is clicked (rowData, responseType)
 * @returns {JSX.Element}
 */
const EnhancedDataTable = ({ data, responseType = "default", onRowClick }) => {
    const config = TABLE_CONFIGS[responseType] || TABLE_CONFIGS.default;
    const [expandedRows, setExpandedRows] = useState(new Set());

    // Extract items from data structure
    const items = useMemo(() => {
        if (Array.isArray(data)) return data;
        if (data?.shipments) return data.shipments;
        if (data?.invoices) return data.invoices;
        if (data?.orders) return data.orders;
        if (data?.results) return data.results;
        return [];
    }, [data]);

    // Auto-generate columns from first item if using "auto" mode
    // Must be called before any conditional returns (React Hooks rule)
    const columns = useMemo(() => {
        if (config.columns === "auto" && items.length > 0) {
            const firstItem = items[0];
            return Object.keys(firstItem).map(key => ({
                key,
                header: formatKey(key),
                width: "w-auto"
            }));
        }
        return config.columns;
    }, [config.columns, items]);

    const toggleExpand = (index) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            next.has(index) ? next.delete(index) : next.add(index);
            return next;
        });
    };

    if (items.length === 0) {
        return (
            <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">
                No results found
            </div>
        );
    }

    const Icon = config.icon;

    return (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                <Icon className="w-4 h-4 text-acs-primary-green" />
                <span className="text-sm font-semibold text-gray-800">
                    {config.title} ({items.length})
                </span>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            {config.expandable && <th className="w-8"></th>}
                            {columns.map((col, idx) => (
                                <th
                                    key={idx}
                                    className={`px-3 py-2 text-left font-semibold text-gray-600 ${col.width || ""}`}
                                >
                                    {col.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((row, rowIdx) => (
                            <React.Fragment key={rowIdx}>
                                <tr
                                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                                    onClick={() => toggleExpand(rowIdx)}
                                >
                                    {config.expandable && (
                                        <td
                                            className="px-2 py-2 expand-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleExpand(rowIdx);
                                            }}
                                        >
                                            {expandedRows.has(rowIdx) ?
                                                <ChevronUp className="w-4 h-4 text-gray-400" /> :
                                                <ChevronDown className="w-4 h-4 text-gray-400" />
                                            }
                                        </td>
                                    )}
                                    {columns.map((col, colIdx) => {
                                        const value = getNestedValue(row, col.key);
                                        return (
                                            <td
                                                key={colIdx}
                                                className={`px-3 py-2 ${col.align === "right" ? "text-right" : ""} ${col.bold ? "font-semibold" : ""} ${col.color || "text-gray-800"}`}
                                            >
                                                {renderCell(value, row, col)}
                                            </td>
                                        );
                                    })}
                                </tr>

                                {config.expandable && expandedRows.has(rowIdx) && (
                                    <tr>
                                        <td colSpan={columns.length + 1} className="p-0">
                                            <div className="bg-gray-50 px-3 py-2">
                                                {config.expandedView === "shipment" && <ShipmentCard data={row} onClick={(data) => onRowClick && onRowClick(data, responseType)} />}
                                                {config.expandedView === "invoice" && <InvoiceCard data={row} onClick={(data) => onRowClick && onRowClick(data, responseType)} />}
                                                {config.expandedView === "order" && <OrderCard data={row} onClick={(data) => onRowClick && onRowClick(data, responseType)} />}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

/**
 * Get nested value from object, supports fallback keys.
 */
const getNestedValue = (obj, key) => {
    if (Array.isArray(key)) {
        for (const k of key) {
            const val = obj?.[k];
            if (val !== undefined && val !== null) return val;
        }
        return null;
    }
    return obj?.[key];
};

/**
 * Render a cell value based on column configuration.
 */
const renderCell = (value, row, col) => {
    if (value == null) return "-";

    // Apply custom formatter
    if (col.formatter) {
        return col.formatter(value, row);
    }

    // Render as badge
    if (col.render === "badge" && col.getBadgeColor) {
        const colorClass = col.getBadgeColor(value);
        return (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
                {value}
            </span>
        );
    }

    // Default: convert to string
    return String(value);
};

/**
 * Format a key for display (convert camelCase to Title Case).
 */
const formatKey = (key) => {
    return key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .replace(/_/g, " ")
        .trim();
};

export default EnhancedDataTable;
